import { Box } from '@wix/design-system';
import type { FC } from 'react';
import React, { useContext, useMemo, useState } from 'react';

import { EditorContext, ModalServiceContext } from './../../ricos-context';
import type { Link_Rel, LinkPreviewData } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { Link_Target } from './../../ricos-schema/legacy/wix/rich_content/v1';
import { FloatingLayout, FooterActions, LinkSettings } from './../../ui-components';

import { useBiNext } from '../../_shared/bi-context-provider/bi-context-provider';
import { linkPreviewModals } from '../consts';
import { LINK_PREVIEW_TYPE } from '../types';

type SettingsModalProps = {
  nodeId: string;
};

export const SettingsModal: FC<SettingsModalProps> = ({ nodeId }) => {
  const modalService = useContext(ModalServiceContext) || {};
  const { getPublicCommands } = useContext(EditorContext);

  const initialData = useMemo(
    () => getPublicCommands().getBlockComponentData(nodeId) as LinkPreviewData,
    []
  );
  const [componentData, setComponentData] = useState<LinkPreviewData>(initialData);

  const logBi = useBiNext({
    pluginId: 'linkPreview',
    modalId: linkPreviewModals.settings,
    nodeId,
  });

  const updateData = (data: LinkPreviewData) => {
    getPublicCommands().setBlock(nodeId, LINK_PREVIEW_TYPE, {
      ...componentData,
      ...data,
    });
    setComponentData({ ...componentData, ...data });
  };

  const closeModal = () => {
    modalService.closeModal(linkPreviewModals.settings);
  };

  const handleOnCancel = () => {
    updateData(initialData);
    closeModal();
  };

  return componentData ? (
    <FloatingLayout
      onCloseButtonClick={handleOnCancel}
      content={{
        children: (
          <Box padding="SP4">
            <LinkSettings
              linkValue={{
                url: componentData.link?.url,
                openInNewTab: componentData.link?.target === Link_Target.BLANK,
                nofollow: componentData.link?.rel?.nofollow,
                noreferrer: componentData.link?.rel?.noreferrer,
                sponsored: componentData.link?.rel?.sponsored,
              }}
              options={{
                showInputField: false,
                showNewTabCheckbox: true,
                showNoReferrerCheckbox: true,
                showNoFollowCheckbox: true,
                showSponsoredCheckbox: true,
              }}
              onChange={data => {
                setComponentData(prev => {
                  const rel: Link_Rel | undefined =
                    data.nofollow || data.noreferrer || data.sponsored
                      ? {
                          nofollow: getValueOrDefault(data.nofollow),
                          noreferrer: getValueOrDefault(data.noreferrer),
                          sponsored: getValueOrDefault(data.sponsored),
                        }
                      : undefined;

                  return {
                    ...prev,
                    link: {
                      ...prev.link,
                      url: prev.link?.url,
                      anchor: prev.link?.anchor,
                      target: data.openInNewTab ? Link_Target.BLANK : Link_Target.TOP,
                      rel,
                    },
                  };
                });
              }}
            />
          </Box>
        ),
      }}
      footer={{
        children: (
          <FooterActions
            onSave={() => {
              updateData(componentData);
              closeModal();
            }}
            onCancel={handleOnCancel}
            logBi={logBi}
          />
        ),
      }}
    />
  ) : null;
};

const getValueOrDefault = (value: boolean | undefined) => (value ? value : undefined);
